// TODO: If you make edits to this file you must deal with these ESLint rules
/*eslint-disable @typescript-eslint/no-use-before-define */

import React from 'react';

import styled from '@emotion/styled';
import { Trans } from '@lingui/macro';

import {
  BREAKPOINTS,
  black,
  disabledColor,
  getColWidthCalc,
  primaryColor,
  unit,
  unitTRBL,
  white,
} from '@zego/theme';
import { useTracking } from '@zego/tracking';

import H1 from '@zego/components/atoms/H1';
import RegularText from '@zego/components/atoms/RegularText';
import View from '@zego/components/atoms/View';
import ButtonLink from '@zego/components/button/ButtonLink';
import ContentContainer from '@zego/components/ContentContainer';
import CoverByWakam from '@zego/components/CoverByWakam';
import Logo from '@zego/components/header/Logo';
import LandingPageHero from '@zego/components/promotions/LandingPageHero';
import ScrollToElement from '@zego/components/ScrollToElement';
import VerticalHairline from '@zego/components/VerticalHairline';

import LandingPageFooter from '../components/LandingPageFooter';

import TrustpilotColor from '@zego/static/images/TrustpilotColor.png';
import irelandHeroImage from '@zego/static/ireland/hero-ireland-taxi-landing.jpg';

export default function IrelandTaxiLandingWrapper({ children }) {
  const { trackEvent } = useTracking();

  return (
    <View css={{ backgroundColor: white }}>
      <ContentContainer
        css={{
          zIndex: 1000,
          position: 'relative',
          [BREAKPOINTS.normal]: {
            paddingTop: 50,
          },
        }}
      >
        <View
          css={{
            flexGrow: 0,
            paddingTop: unit(5),
            fill: white,
            marginBottom: unit(3),
            [BREAKPOINTS.medium]: {
              paddingTop: unit(2),
            },
          }}
        >
          <Logo css={{ width: 103, height: 28 }} />
        </View>
      </ContentContainer>

      <LandingPageHero image={irelandHeroImage}>
        <View
          css={{
            [BREAKPOINTS.medium]: { width: getColWidthCalc(12, 20, 7) },
          }}
        >
          <Title>New taxi insurance from Zego</Title>
          <Subtitle>
            Zego's taxi insurance is designed for SPSV drivers in Ireland. Our
            cover offers you great protection at competitive prices.
          </Subtitle>
        </View>
        <CoverByWakam />
      </LandingPageHero>
      {/* Overall wrapper */}
      <View
        css={{
          backgroundColor: disabledColor(2),
          marginBottom: unit(6),
          [BREAKPOINTS.medium]: {
            padding: unitTRBL(5, 8, 5, 8),
          },
        }}
      >
        <ContentContainer
          css={{
            paddingBottom: unit(2),
            [BREAKPOINTS.medium]: {
              display: 'flex',
              justifyContent: 'space-between',
              flexDirection: 'row-reverse',
              paddingBottom: 0,
            },
          }}
        >
          {/* Right side wrappers */}
          <View
            css={{
              width: '100%',
              position: 'relative',
              [BREAKPOINTS.medium]: {
                width: getColWidthCalc(12, 20, 5),
                marginTop: '-120px',
              },
            }}
          >
            {children}
            <View
              css={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                paddingTop: unit(7.5),
                [BREAKPOINTS.medium]: {
                  paddingTop: unit(2.5),
                },
              }}
            >
              <img
                src={TrustpilotColor}
                alt="Trust Pilot Logo"
                css={{ width: 105 }}
              />
              <VerticalHairline
                css={{
                  display: 'block',
                  marginLeft: unit(2),
                  marginRight: unit(2),
                }}
                color={disabledColor(7)}
                height="26px"
              />
              <RegularText
                css={{
                  fontSize: 10,
                  color: black,
                  lineHeight: '16px',
                  position: 'relative',
                  top: '4px',
                }}
              >
                <Trans id="Global / Trust Pilot Review Percentage">
                  92% of our customers rate us 'great' or 'excellent'
                </Trans>
              </RegularText>
            </View>
          </View>
          {/* Left side */}
          <View
            css={{
              display: 'flex',
              flexDirection: 'column-reverse',
              [BREAKPOINTS.medium]: {
                width: getColWidthCalc(12, 20, 7),
                flexDirection: 'column',
              },
            }}
          >
            <View>
              <RegularText
                css={{
                  display: 'none',
                  [BREAKPOINTS.medium]: {
                    display: 'block',
                    color: disabledColor(10),
                    fontWeight: 'normal',
                    fontSize: 18,
                    lineHeight: '24px',
                    paddingRight: unit(7),
                  },
                }}
              >
                Zego’s taxi insurance gives you peace of mind while driving and
                may reduce the cost of your cover. Combined with technology,
                our cover protects you against inflated or fraudulent claims
                and it means you will always be able to submit proof in the
                event of an incident.
              </RegularText>
              {/* Start bullet point container */}
              <div
                css={{
                  width: '100%',
                  [BREAKPOINTS.medium]: {
                    width: getColWidthCalc(7, 20, 5),
                  },
                }}
              >
                <RegularText
                  css={{
                    color: disabledColor(10),
                    fontWeight: 'bold',
                    fontSize: 18,
                    lineHeight: '24px',
                    textAlign: 'center',
                    paddingBottom: unit(4.5),
                    marginLeft: 'auto',
                    marginRight: 'auto',
                    marginTop: unit(6),
                    [BREAKPOINTS.medium]: {
                      marginTop: unit(3),
                      textAlign: 'left',
                      width: '100%',
                      paddingBottom: unit(2),
                    },
                  }}
                >
                  How it works
                </RegularText>
                <View
                  css={{
                    display: 'flex',
                    flexDirection: 'column',
                    marginLeft: '18px',
                    marginBottom: unit(2.5),
                  }}
                >
                  <ul>
                    <FeatureListItem>
                      Get a free device (dashcam or plug-in device) from Zego
                      when you purchase your cover
                    </FeatureListItem>
                    <FeatureListItem>
                      Easily set up your device on your vehicle
                    </FeatureListItem>
                    <FeatureListItem>
                      Automatically send vital information to your insurer in
                      the event of an incident
                    </FeatureListItem>
                    <FeatureListItem>
                      Benefit from a simplified claims process
                    </FeatureListItem>
                  </ul>
                </View>
              </div>
              {/* End bullet point container */}
            </View>
            <RegularText
              css={{
                display: 'none',
                [BREAKPOINTS.medium]: {
                  display: 'block',
                  fontWeight: 500,
                  fontSize: '18px',
                  lineHeight: '24px',
                  color: disabledColor(10),
                },
              }}
            >
              Want to know more about the benefits of our product?
            </RegularText>
          </View>
        </ContentContainer>
      </View>
      <ContentContainer
        css={{
          display: 'block',
          [BREAKPOINTS.medium]: {
            display: 'none',
          },
        }}
      >
        <RegularText
          css={{
            color: disabledColor(10),
            fontSize: 20,
            textAlign: 'center',
            fontWeight: 500,
            marginBottom: unit(6),
          }}
        >
          Want to know more about the benefits of our product?
        </RegularText>
        <ScrollToElement
          selector="#ireland-landing-form"
          render={scroll => (
            <ButtonLink
              onClick={event => {
                event.preventDefault();
                scroll();
                trackEvent(
                  'Ireland Taxi Landing Page, Get Quote Scroll to Top - Button Clicked',
                );
              }}
              backgroundColor={primaryColor(11)}
              css={{
                width: '100%',
                marginLeft: 0,
                padding: unitTRBL(3, 0, 3, 0),
                fontSize: 18,
                fontWeight: 'bold',
              }}
            >
              Find out more
            </ButtonLink>
          )}
        />
      </ContentContainer>
      <LandingPageFooter
        disclaimerText="*10% of our customers paid this or less in December 2018 and January
        2019."
      />
    </View>
  );
}

const Title = styled(H1)({
  color: white,
  fontSize: '34px',
  fontWeight: 'bold',
  letterSpacing: '-1.06px',
  lineHeight: '40px',
  marginBottom: unit(3),
  [BREAKPOINTS.medium]: {
    lineHeight: '52px',
    letterSpacing: '-1.71px',
    fontSize: 48,
  },
});

const Subtitle = styled(RegularText)({
  color: white,
  fontSize: 18,
  fontWeight: 'bold',
  letterSpacing: '-0.73px',
  lineHeight: '23px',
  marginBottom: 22,
  [BREAKPOINTS.medium]: {
    fontSize: 23,
    letterSpacing: '-0.94px',
    lineHeight: '28px',
  },
});

const FeatureListItem = styled('li')({
  listStyleType: 'disc',
  listStylePosition: 'outside',
  fontSize: 18,
  lineHeight: '24px',
  paddingLeft: 18,
  letterSpacing: '-0.16px',
  marginBottom: 15,
});
